import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { KIND_LABEL, useStartInspection, type InspectionKind, type InspectionRegister } from "../../api/inspections";

/** Raise an inspection from a works order that did not come through a route
 * plan: the contract, the number and, when known, the kind of work, and the new
 * draft opens. The kind of work is sent as the work type, so the server's rule
 * -- an assessment or a replacement is PM41 work -- decides a crew's form. The
 * office also chooses the form: the server's default, a PM42 service unless the
 * release setting says otherwise (Phase 1 decision 4), or a PM41 inspection. It
 * follows the kind of work (`pm41WorkTypes`, from the register) until the
 * office changes it; a repair is raised by choosing "PM41 inspection". */
export default function RaiseInspection({
  contracts,
  workTypes,
  pm41WorkTypes,
  office,
  defaultKind,
  onRaised,
}: {
  contracts: InspectionRegister["contracts"];
  workTypes: InspectionRegister["work_types"];
  pm41WorkTypes: string[];
  office: boolean;
  defaultKind: InspectionKind;
  onRaised: () => void;
}) {
  const navigate = useNavigate();
  const start = useStartInspection();
  const [contractId, setContractId] = useState(contracts.length === 1 ? String(contracts[0].id) : "");
  const [workOrder, setWorkOrder] = useState("");
  const [workType, setWorkType] = useState("");
  const [kind, setKind] = useState<InspectionKind>(defaultKind);

  function chooseWorkType(value: string) {
    setWorkType(value);
    setKind(pm41WorkTypes.includes(value) ? "pm41" : defaultKind);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    start.mutate(
      {
        contract_id: Number(contractId),
        work_order_no: workOrder.trim(),
        ...(workType ? { work_type: workType } : {}),
        ...(office ? { kind } : {}),
      },
      {
        onSuccess: (detail) => {
          onRaised();
          navigate(`/inspections/${detail.inspection.id}`);
        },
      },
    );
  }

  return (
    <form className="insp-raise" onSubmit={submit} aria-label="Raise an inspection">
      <h3>Raise an inspection</h3>
      <label className="field">
        Contract
        <select value={contractId} onChange={(e) => setContractId(e.target.value)} required>
          <option value="">Choose…</option>
          {contracts.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code} {c.name}
            </option>
          ))}
        </select>
      </label>
      <label className="field">
        Works order / notification no.
        <input value={workOrder} onChange={(e) => setWorkOrder(e.target.value)} required autoFocus />
      </label>
      <label className="field">
        Kind of work
        <select value={workType} onChange={(e) => chooseWorkType(e.target.value)}>
          <option value="">Not stated yet</option>
          {workTypes.map((wt) => (
            <option key={wt.value} value={wt.value}>
              {wt.label}
            </option>
          ))}
        </select>
      </label>
      {office ? (
        <label className="field">
          Form
          <select value={kind} onChange={(e) => setKind(e.target.value as InspectionKind)}>
            <option value="pm42">{KIND_LABEL.pm42}</option>
            <option value="pm41">{KIND_LABEL.pm41}</option>
          </select>
        </label>
      ) : null}
      <button type="submit" className="btn btn-primary" disabled={start.isPending || contractId === "" || workOrder.trim() === ""}>
        {start.isPending ? "Raising…" : "Raise"}
      </button>
      {start.isError ? <p className="notice bad">{start.error.message}</p> : null}
    </form>
  );
}
